import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { UserRole } from "@/lib/schema";

interface UseLandingRedirectReturn {
  shouldShowLanding: boolean;
  isRedirecting: boolean;
}

/**
 * Custom hook to handle authentication-based redirection logic for the landing page.
 * This hook determines whether to show the landing page or redirect authenticated users
 * to the appropriate dashboard based on their role.
 */
export function useLandingRedirect(): UseLandingRedirectReturn {
  const [_, setLocation] = useLocation();
  const [userDataTimeout, setUserDataTimeout] = useState(false);
  const { currentUser, userData, loading } = useAuth();

  // Handle authenticated users with complete data
  useEffect(() => {
    if (!loading && currentUser) {
      if (userData) {
        // User has both auth and Firestore data - normal redirect
        const target = (userData.role === UserRole.SUPER_ADMIN || userData.role === UserRole.RECRUITER) 
          ? '/admin' 
          : '/dashboard';
        setLocation(target);
      } else {
        // User is authenticated but no userData - set a timeout
        const timeout = setTimeout(() => {
          setUserDataTimeout(true);
        }, 3000); // Wait 3 seconds for userData to load

        return () => clearTimeout(timeout);
      }
    }
  }, [loading, currentUser, userData, setLocation]);

  // Fallback redirect for authenticated users with missing userData after timeout
  useEffect(() => {
    if (userDataTimeout && currentUser && !userData) {
      console.warn('User authenticated but userData missing after timeout - redirecting to dashboard');
      setLocation('/dashboard');
    }
  }, [userDataTimeout, currentUser, userData, setLocation]);

  // Determine if we should show the landing page
  const shouldShowLanding = !loading && !currentUser;
  
  // Determine if we're in a redirecting state
  const isRedirecting = loading || 
    (!!currentUser && !!userData) || 
    (!!currentUser && userDataTimeout);

  return {
    shouldShowLanding,
    isRedirecting
  };
}
